import { PersonType } from '../services/models/enums';

/**
 * Returns the appropriate document mask based on the person type
 * @param personType - The person type (PERSON for CPF, COMPANY for CNPJ)
 * @returns Mask string for use with ngx-mask
 */
export function getDocumentMask(personType: PersonType): string {
  return personType === PersonType.PERSON ? '000.000.000-00' : 'AA.AAA.AAA/AAAA-00';
}

/**
 * Validates a Brazilian document (CPF or CNPJ) and returns a structured result
 * @param value - The document value to be validated (may contain formatting)
 * @returns Object with isValid and errorMessage
 */
export function validateBrazilianDocument(value: string): {
  isValid: boolean;
  errorMessage?: string;
} {
  if (!value) {
    return { isValid: true };
  }

  const cleanValue = value.toUpperCase().replace(/[^\dA-Z]/g, '');

  if (cleanValue.length === 11 && /^\d+$/.test(cleanValue)) {
    const isValid = CPFValidate(cleanValue);
    return {
      isValid,
      errorMessage: isValid ? undefined : $localize`:@@invalidDocument:Documento inválido`,
    };
  }

  if (cleanValue.length === 14) {
    const isValid = CNPJValidate(cleanValue);
    return {
      isValid,
      errorMessage: isValid ? undefined : $localize`:@@invalidDocument:Documento inválido`,
    };
  }

  if (cleanValue.length > 0) {
    return {
      isValid: false,
      errorMessage: $localize`:@@invalidDocument:Documento inválido`,
    };
  }

  return { isValid: true };
}

export function CPFValidate(cpf: string) {
  cpf = cpf.replace(/\D/g, '');

  if (cpf.length !== 11) {
    return false;
  }

  if (/^(\d)\1{10}$/.test(cpf)) {
    return false;
  }

  // Validação dos dígitos verificadores
  let soma = 0;
  let resto;
  for (let i = 1; i <= 9; i++) {
    soma += parseInt(cpf.substring(i - 1, i)) * (11 - i);
  }
  resto = (soma * 10) % 11;
  if (resto === 10 || resto === 11) {
    resto = 0;
  }
  if (resto !== parseInt(cpf.substring(9, 10))) {
    return false;
  }

  soma = 0;
  for (let i = 1; i <= 10; i++) {
    soma += parseInt(cpf.substring(i - 1, i)) * (12 - i);
  }
  resto = (soma * 10) % 11;
  if (resto === 10 || resto === 11) {
    resto = 0;
  }
  if (resto !== parseInt(cpf.substring(10, 11))) {
    return false;
  }

  return true;
}

export function CNPJValidate(cnpj: string): boolean {
  cnpj = cnpj.toUpperCase().replace(/[^\dA-Z]/g, '');

  if (cnpj.length !== 14) {
    return false;
  }

  // The first 12 chars can be Alphanumeric, the last 2 must be numbers
  const base = cnpj.substring(0, 12);
  const dv = cnpj.substring(12, 14);
  if (!/^[\dA-Z]{12}$/.test(base) || !/^\d{2}$/.test(dv)) {
    return false;
  }

  if (/^(.)\1+$/.test(cnpj)) {
    return false;
  }

  // Calcular os dígitos verificadores
  let tamanho = cnpj.length - 2;
  let numeros = cnpj.substring(0, tamanho);
  const digitos = cnpj.substring(tamanho);
  let soma = 0;
  let pos = tamanho - 7;

  for (let i = tamanho; i >= 1; i--) {
    const val = numeros.charCodeAt(tamanho - i) - 48;
    soma += val * pos--;
    if (pos < 2) {
      pos = 9;
    }
  }

  let resultado = soma % 11 < 2 ? 0 : 11 - (soma % 11);

  if (resultado !== parseInt(digitos.charAt(0))) {
    return false;
  }

  tamanho = tamanho + 1;
  numeros = cnpj.substring(0, tamanho);
  soma = 0;
  pos = tamanho - 7;

  for (let i = tamanho; i >= 1; i--) {
    const val = numeros.charCodeAt(tamanho - i) - 48;
    soma += val * pos--;
    if (pos < 2) {
      pos = 9;
    }
  }

  resultado = soma % 11 < 2 ? 0 : 11 - (soma % 11);

  if (resultado !== parseInt(digitos.charAt(1))) {
    return false;
  }

  return true;
}
